import { Link } from "@tanstack/react-router";
import type { PublicTeam } from "@/lib/types";
import {
  LiveStatusPill,
  type LiveStatusPillState,
} from "@/features/live/components/LiveStatusPill";

/**
 * `TeamCard` — shared public/member directory card for `/teams`.
 *
 * Brand kit consumer: every color/spacing routes through tokens
 * (`bg-surface`, `border-rule`, `text-ink`, etc.) so the card
 * inherits light + dark theme parity from `tokens.css`.
 *
 * The card surfaces the slim directory payload: name, slug, match count,
 * visibility when supplied, and the latest played match date. The optional
 * live pill comes only from the public aggregate poll; private cards never
 * infer "offline" from an aggregate that deliberately excludes them. The
 * whole card stays one Link/tap target.
 */
export function TeamCard({
  team,
  liveState,
  visibility,
}: {
  team: PublicTeam;
  liveState?: LiveStatusPillState;
  visibility?: "public" | "private";
}) {
  return (
    <Link
      to="/teams/$teamSlug"
      params={{ teamSlug: team.slug }}
      data-testid="team-card"
      data-team-slug={team.slug}
      className="group flex h-full flex-col gap-3 rounded-panel border border-rule bg-surface p-5 transition-colors hover:border-accent"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h3 className="min-w-0 flex-1 font-display text-lg leading-snug tracking-tight text-ink group-hover:text-accent">
          {team.name}
        </h3>
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {visibility ? (
            <span
              data-testid="team-card-visibility"
              className={
                visibility === "public"
                  ? "rounded-pill border border-accent px-2 py-0.5 font-mono text-[0.6rem] uppercase tracking-wider text-accent"
                  : "rounded-pill border border-rule bg-elevated px-2 py-0.5 font-mono text-[0.6rem] uppercase tracking-wider text-ink-muted"
              }
            >
              {visibility === "public" ? "Public" : "Private"}
            </span>
          ) : null}
          {liveState ? <LiveStatusPill state={liveState} /> : null}
          <span className="rounded-pill border border-rule px-2 py-0.5 font-mono text-[0.6rem] uppercase tracking-wider text-ink-muted">
            {team.game_format}
          </span>
        </div>
      </div>
      <dl className="flex flex-wrap gap-x-5 gap-y-1 font-mono text-xs text-ink-muted">
        <div className="flex items-center gap-1.5">
          <dt className="uppercase tracking-wider">Matches</dt>
          <dd
            data-testid="team-card-match-count"
            className="font-display text-ink"
          >
            {team.match_count}
          </dd>
        </div>
        {team.last_match_at ? (
          <div className="flex items-center gap-1.5">
            <dt className="uppercase tracking-wider">Last</dt>
            <dd data-testid="team-card-last-at" className="text-ink">
              {team.last_match_at}
            </dd>
          </div>
        ) : null}
      </dl>
      <span
        aria-hidden="true"
        className="mt-auto font-mono text-[0.6rem] uppercase tracking-mono text-ink-muted group-hover:text-ink"
      >
        open team →
      </span>
    </Link>
  );
}
